/**
 * The sky, and eventually the weather that was in it.
 *
 * Two maps draw a tilted 3D scene and both were rendering a white void above the horizon. The
 * cause is `atmosphere-blend`: MapLibre GL 6 will not draw the sky dome at all without it, and
 * a `setSky` call that sets only colours leaves the background showing through. `fog-color` is
 * additionally documented as **requiring 3D terrain** — with terrain off it is simply ignored,
 * which is why the same settings looked fine on one map and not the other.
 *
 * The property set is MapLibre 6's, read from its own style-spec typings rather than from an
 * example: `sky-color`, `horizon-color`, `fog-color`, `sky-horizon-blend`, `horizon-fog-blend`,
 * `fog-ground-blend`, `atmosphere-blend`.
 */

import type { SkySpecification } from 'maplibre-gl';

/**
 * Where the gradient starts to appear, and where it is fully there.
 *
 * The sky dome is drawn at every pitch, because without one a tilted scene ends in the page
 * background — the white void this file exists to fix. But *drawn* and *visible as a gradient*
 * are different things, and at the default viewing angle a full haze gradient took a quarter of
 * the frame and made every flight look like it happened in fog.
 *
 * So the dome stays and the gradient is what fades in: below `PITCH_MIN` the sky is a single
 * flat tone that reads as a backdrop, and between there and `PITCH_FULL` the horizon colour,
 * the warmth and the haze arrive as the camera is laid over. Tilt further, get more sky —
 * which is also what actually happens when you look out of a cockpit.
 */
const PITCH_MIN = 56;
const PITCH_FULL = 76;

/** What the sky needs to know about the day. Everything optional: no data means a clear day. */
export interface SkyWeather {
  /** Total cloud cover, 0..100. */
  readonly cloudCover?: number | null | undefined;
  /**
   * Metres. Low visibility thickens the haze and pulls the horizon in.
   *
   * Frequently null, and that is expected rather than a fault: ERA5 has no visibility field at
   * all, so any flight old enough to fall back to the archive has none.
   */
  readonly visibilityM?: number | null | undefined;
  /** Hour of the day in UTC, 0..23, for the warmth of the light. */
  readonly hourUtc?: number | null | undefined;
}

const mix = (a: string, b: string, t: number): string => {
  const parse = (h: string): [number, number, number] => [
    Number.parseInt(h.slice(1, 3), 16),
    Number.parseInt(h.slice(3, 5), 16),
    Number.parseInt(h.slice(5, 7), 16),
  ];
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `#${[c(r1, r2), c(g1, g2), c(b1, b2)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** Towards black by `amount`, keeping the hue. */
const darken = (hex: string, amount: number): string => mix(hex, '#000000', clamp01(amount));

/**
 * A sky for the conditions.
 *
 * The mapping is deliberately simple and monotonic: cloud cover moves every colour from a deep
 * blue towards a flat grey, and poor visibility raises the fog blend so the far terrain washes
 * out. It is an impression of the day, not a simulation — and an impression that is wrong in a
 * legible direction (overcast looks overcast) beats a fixed blue that is wrong every time.
 */
export function skyFor(weather: SkyWeather = {}, pitch = 90): SkySpecification {
  const cloud = clamp01((weather.cloudCover ?? 0) / 100);
  // 40 km is "you can see the Alps"; 5 km is a summer haze you cannot see the next ridge in.
  const haze = clamp01(1 - (weather.visibilityM ?? 40_000) / 40_000);

  const hour = weather.hourUtc ?? 12;
  // Warm low sun, neutral at midday. Only the horizon takes it; the zenith stays cold.
  const lowSun = clamp01((Math.abs(hour - 12.5) - 3.5) / 3);

  // How much of the sky the camera angle has earned. Defaults to fully revealed so a caller
  // that does not care about pitch gets the old behaviour.
  const reveal = clamp01((pitch - PITCH_MIN) / (PITCH_FULL - PITCH_MIN));

  const zenith = mix('#0b1a33', '#2a2f36', cloud * 0.9);
  const litHorizon = mix(mix('#5f86b4', '#565c64', cloud), '#b8875e', lowSun * (1 - cloud * 0.6));
  // Pulled back towards the zenith when the camera is barely tilted: same colour top and bottom
  // is a flat backdrop, and a gradient needs two ends to be one.
  const horizon = mix(zenith, litHorizon, reveal);

  /**
   * The fog is the **sky**, darkened — not a pale grey of its own.
   *
   * It used to be a fixed `#b9cbe0`, a light blue-grey borrowed from how haze looks in daylight.
   * Over dark satellite imagery on a dark interface that reads as milk poured across the top
   * third of the map, and it was the first thing anybody noticed about a tilted view. Distant
   * ground should recede *into the sky it is under*, which is also what it does in the air, so
   * the colour is derived from the horizon and taken down a quarter.
   */
  const fog = darken(mix(horizon, zenith, 0.35), 0.25 - haze * 0.15);

  return {
    'sky-color': zenith,
    'horizon-color': horizon,
    // Only has an effect with terrain enabled, which both 3D views have.
    'fog-color': fog,
    // Overcast has no gradient to speak of; a clear day has a long one. Scaled by the reveal so
    // that at a shallow angle the transition is tight and sits right on the horizon line
    // instead of washing down over the terrain.
    'sky-horizon-blend': (0.45 + cloud * 0.35) * (0.25 + reveal * 0.75),
    // How far up from the horizon the fog reaches. Kept low: this is the band that was eating
    // the top of the map, and on a clear day there is barely any haze to justify it.
    'horizon-fog-blend': (0.12 + haze * 0.38) * (0.3 + reveal * 0.7),
    // How far *down* the fog comes towards the camera. Zero on a clear day — the ground twenty
    // kilometres away on a 40 km-visibility afternoon is not obscured, and drawing it that way
    // is the difference between depth and a smear.
    'fog-ground-blend': haze * 0.3 * reveal,
    // Never zero. Without this the dome is not drawn at all and the scene ends in the page
    // background — the white void that started all of this — so the floor stays well clear of
    // it and only the gradient above fades. Cloud flattens it slightly so an overcast sky stops
    // reading as a gradient.
    'atmosphere-blend': (0.4 + 0.6 * reveal) * (1 - cloud * 0.15),
  };
}

/** A one-line description for the UI, so the sky is labelled rather than merely atmospheric. */
export function describeSky(weather: SkyWeather): string | null {
  if (weather.cloudCover === undefined || weather.cloudCover === null) return null;
  const c = weather.cloudCover;
  const cover =
    c < 12 ? 'wolkenlos' : c < 38 ? 'gering bewölkt' : c < 63 ? 'wolkig' : c < 88 ? 'stark bewölkt' : 'bedeckt';
  if (weather.visibilityM != null && weather.visibilityM < 8000) {
    return `${cover}, diesig (${Math.round(weather.visibilityM / 1000)} km Sicht)`;
  }
  return cover;
}
